import { useState, useRef, useCallback } from 'react';
import { FileSignature, Check, RotateCcw } from 'lucide-react';
import { useParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function ConsentPage() {
    const { consentId } = useParams<{ consentId: string }>();
    const [signed, setSigned] = useState(false);
    const [signing, setSigning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasDrawn, setHasDrawn] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isDrawing = useRef(false);

    const getCtx = () => canvasRef.current?.getContext('2d') ?? null;

    const startDrawing = useCallback((x: number, y: number) => {
        isDrawing.current = true;
        const ctx = getCtx();
        if (!ctx) return;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
    }, []);

    const draw = useCallback((x: number, y: number) => {
        if (!isDrawing.current) return;
        const ctx = getCtx();
        if (!ctx) return;
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasDrawn(true);
    }, []);

    const stopDrawing = useCallback(() => { isDrawing.current = false; }, []);

    const getPos = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current!;
        const rect = canvas.getBoundingClientRect();
        if ('touches' in e) {
            return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const clearCanvas = () => {
        const ctx = getCtx();
        if (ctx && canvasRef.current) {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            setHasDrawn(false);
        }
    };

    const handleSign = async () => {
        if (!canvasRef.current || !consentId) return;
        setSigning(true);
        setError(null);
        try {
            const signatureData = canvasRef.current.toDataURL('image/png');
            const res = await fetch(`${API_BASE}/encounters/consent/${consentId}/sign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ signatureData }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: 'Error al firmar' }));
                throw new Error(err.error || `Error ${res.status}`);
            }
            setSigned(true);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Error al firmar el consentimiento');
        } finally {
            setSigning(false);
        }
    };

    if (signed) {
        return (
            <div className="fade-in">
                <div className="portal-header">
                    <h1>Consentimiento</h1>
                    <p>Formulario de consentimiento informado</p>
                </div>
                <div className="portal-content">
                    <div className="portal-card" style={{ textAlign: 'center', padding: 32 }}>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <Check size={32} color="#059669" />
                        </div>
                        <h2 style={{ marginBottom: 8 }}>¡Consentimiento Firmado!</h2>
                        <p style={{ color: 'var(--color-text-muted)' }}>
                            Se ha registrado su firma digital. Recibirá una copia por WhatsApp.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fade-in">
            <div className="portal-header">
                <h1>Consentimiento Informado</h1>
                <p>Lea y firme el consentimiento para su procedimiento</p>
            </div>
            <div className="portal-content">
                {/* Consent Text */}
                <div className="portal-card" style={{ padding: 20, maxHeight: 300, overflow: 'auto', marginBottom: 16, fontSize: 13, lineHeight: 1.6, color: 'var(--color-text-secondary)' }}>
                    <h3 style={{ marginBottom: 8, color: 'var(--color-text-primary)' }}>Consentimiento para Tratamiento Médico General</h3>
                    <p>Yo, el/la paciente abajo firmante, autorizo al equipo médico de ClinicOS Demo Clinic a realizar la evaluación, diagnóstico y tratamiento médico que consideren necesario para mi condición de salud.</p>
                    <p style={{ marginTop: 8 }}>Entiendo que:</p>
                    <ul style={{ paddingLeft: 20, marginTop: 4 }}>
                        <li>Se me ha explicado la naturaleza del tratamiento propuesto</li>
                        <li>He sido informado(a) sobre los riesgos y beneficios</li>
                        <li>Tengo derecho a solicitar información adicional en cualquier momento</li>
                        <li>Me reservo el derecho a revocar este consentimiento</li>
                        <li>Mi información médica será tratada de forma confidencial según la Ley 1581 de 2012</li>
                    </ul>
                    <p style={{ marginTop: 8 }}>Al firmar este documento, confirmo que he leído, entiendo y acepto voluntariamente los términos anteriores.</p>
                </div>

                {/* Signature Canvas */}
                <div className="portal-card" style={{ padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                            <FileSignature size={16} /> Firma Digital
                        </label>
                        {hasDrawn && (
                            <button onClick={clearCanvas} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <RotateCcw size={12} /> Limpiar
                            </button>
                        )}
                    </div>
                    <canvas
                        ref={canvasRef}
                        width={320}
                        height={150}
                        style={{ width: '100%', height: 150, border: '2px dashed var(--color-border)', borderRadius: 10, cursor: 'crosshair', touchAction: 'none', background: '#fafafa' }}
                        onMouseDown={e => { const p = getPos(e); startDrawing(p.x, p.y); }}
                        onMouseMove={e => { const p = getPos(e); draw(p.x, p.y); }}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={e => { e.preventDefault(); const p = getPos(e); startDrawing(p.x, p.y); }}
                        onTouchMove={e => { e.preventDefault(); const p = getPos(e); draw(p.x, p.y); }}
                        onTouchEnd={stopDrawing}
                    />
                    <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4, textAlign: 'center' }}>
                        Dibuje su firma con el dedo o ratón
                    </p>

                    <button onClick={handleSign} disabled={!hasDrawn || signing} style={{
                        width: '100%', marginTop: 12, padding: 14, borderRadius: 10, border: 'none',
                        background: hasDrawn && !signing ? 'var(--color-primary)' : '#ccc',
                        color: '#fff', fontSize: 16, fontWeight: 600, cursor: hasDrawn && !signing ? 'pointer' : 'default',
                    }}>
                        {signing ? 'Firmando...' : 'Firmar Consentimiento'}
                    </button>
                    {error && (
                        <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8, textAlign: 'center' }}>{error}</p>
                    )}
                </div>
            </div>
        </div>
    );
}
